import React, { Fragment, useEffect } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress, Tooltip, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, Typography, FormGroup, FormControlLabel, Checkbox } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { Trim } from '../../common/util';
import { IsEmpty } from '../../common/checks';
import { addHighlight, editHighlight, deleteHighlight, editHighlights, updateDisplayOrder } from '../../actions/admin/highlightAction';
import { getPagesByType } from '../../actions/admin/pageAction';
import HighlightTable from './table';
import HighlightSortable from './highlightSortable';
import apiService from '../../services.js/apiService';

const HighlightsList = ({ addHighlight, editHighlight, deleteHighlight, editHighlights, getPagesByType, updateDisplayOrder, token }) => {
  const [loading, setLoading] = React.useState(false);
  const [refresh, setRefresh] = React.useState(false);
  const [sorted, setSorted] = React.useState([]);
  const [highlights, setHighlights] = React.useState([]);
  const [pages, setPages] = React.useState([]);
  const [selection, setSelection] = React.useState([]);
  const [title, setTitle] = React.useState('');
  const [langZh, setLangZh] = React.useState('');
  const [page, setPage] = React.useState('');
  const [openAdd, setOpenAdd] = React.useState(false);
  const [openEdit, setOpenEdit] = React.useState(false);
  const [modalHeight, setModalHeight] = React.useState('');

  const [highlightId, setHighlightId] = React.useState(null);
  const [currentSelectedHighlight, setCurrentSelectedHighlights] = React.useState([]);
  const [isUpdating, setUpdating] = React.useState(false);
  const [isMultipleHighlights, setMultipleHighlights] = React.useState(false);
  const [group, setGroup] = React.useState([]);

  const useStyles = makeStyles(() => ({
    modal: {
      minHeight: modalHeight,
      maxHeight: '80vh',
    },
  }));
  const classes = useStyles();

  useEffect(() => {
    const element = document.getElementById('form-dialog-content');
    if (element) { setModalHeight(element.offsetHeight); }
  }, [openEdit]);

  const fetchHighlights = async () => {
    try {
      const highlightsData = await apiService.getMixedHighlights();
      if (highlightsData) {
        setHighlights(highlightsData);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const fetchViewPages = async () => {
    const pages = await getPagesByType({ type: 'View' });
    if (pages) { setPages(pages); }
  };

  const fetchList = (fetchAll = false) => {
    const selectedPages = [];
    const list = [];
    highlights.forEach((highlight) => selectedPages.push(highlight.pageId));

    if (fetchAll) {
      pages.forEach((pg) => {
        if (!IsEmpty(pg.id)) {
          list.push({ id: pg.id, name: pg.name });
        }
      });
    } else if (isUpdating) {
      /*
      * currentSelectedHighlight.id = refers to current selected Page id.
      */
      if (isMultipleHighlights) {
        pages.forEach((pg) => {
          if (!IsEmpty(pg.id)) {
            list.push({ id: pg.id, name: pg.name });
          }
        });
      } else {
        // push only the unselected pages and the current selected page.
        pages.forEach((pg) => {
          if (!selectedPages.includes(pg.id) || pg.id === currentSelectedHighlight.id) {
            list.push({ id: pg.id, name: pg.name });
          }
        });
      }
    } else if (isMultipleHighlights) {
      pages.forEach((pg) => {
        if (!IsEmpty(pg.id)) {
          list.push({ id: pg.id, name: pg.name });
        }
      });
    } else {
      pages.forEach((pg) => {
        if (!selectedPages.includes(pg.id)) {
          list.push({ id: pg.id, name: pg.name });
        }
      });
    }

    setSelection(list);
  };

  const hasEmptyFields = () => {
    if (IsEmpty(Trim(title)) || (!isMultipleHighlights && IsEmpty(page)) || (isMultipleHighlights && IsEmpty(group))) { return true; }
  };

  const handleOpenAdd = () => {
    fetchList();
    setOpenAdd(true);
  };

  const handleCloseAdd = () => {
    setUpdating(false);
    setOpenAdd(false);
    setHighlightId(null);
    setTitle('');
    setLangZh('');
    setPage('');
    setGroup([]);
    setMultipleHighlights(false);
  };

  const handleOpenEdit = () => {
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  const handleChange = (e) => {
    switch (e.target.name) {
      case 'title':
        setTitle(e.target.value);
        break;
      case 'langZh':
        setLangZh(e.target.value);
        break;
      default:
    }
  };

  const handleSort = (data) => {
    setSorted(data);
  };

  const setHighlightData = (e, data, reason) => {
    switch (reason) {
      case 'select-option':
        if (!IsEmpty(currentSelectedHighlight)) {
          if (isMultipleHighlights && _.some(currentSelectedHighlight, ['id', data[data.length - 1].id])) {
            data = _.filter(currentSelectedHighlight, (d) => d.id !== data[0].id);
          }

          if (isMultipleHighlights) {
            setGroup(data);
          } else {
            setPage(data);
          }
        } else if (isMultipleHighlights) {
          setGroup(data);
        } else {
          setPage(data);
        }

        setCurrentSelectedHighlights(data);
        break;
      case 'remove-option':
        if (!IsEmpty(currentSelectedHighlight)) {
          if (isMultipleHighlights) {
            setGroup(data);
          } else {
            setPage(data);
          }
        } else {
          setCurrentSelectedHighlights([]);
          setGroup([]);
          setPage('');
        }
        break;
      case 'clear':
        setCurrentSelectedHighlights([]);
        setGroup([]);
        setPage('');
        fetchList(true);
        break;
      default:
    }
  };

  const onEditHighlight = async (data) => {
    if (!IsEmpty(data)) {
      fetchList();

      setUpdating(true);
      setOpenAdd(true);

      // set data
      setHighlightId(data.id);
      setTitle(data.title);
      setLangZh(data.translatedPageName['zh-SG']);

      if (!IsEmpty(data.group)) {
        setMultipleHighlights(true);
        setPage('');

        const formattedGroup = [];
        _.forEach(data.group, (g) => {
          const grp = _.assign({
            id:   g.pageId,
            name: g.pageName,
          });

          formattedGroup.push(grp);
        });

        setGroup(formattedGroup);
        setCurrentSelectedHighlights(formattedGroup);
      } else {
        setPage({ id: data.pageId, name: data.pageName });
        setCurrentSelectedHighlights({ id: data.pageId, name: data.pageName });
      }
    }
  };

  const onAddHighlight = async () => {
    setLoading(true);
    const trimmedTitle = Trim(title, false, true);

    if (hasEmptyFields()) {
      toast.error('One of the field(s) is empty.');
      setLoading(false);
      return;
    }

    // title checks
    if (!isUpdating && _.some(highlights, ['title', title])) {
      toast.error('Title already exist');
      setLoading(false);
      return;
    } else {
      // check if the title is updated if so, check its uniqueness.
      const current = _.filter(highlights, (h) => h.id === highlightId);
      if (!IsEmpty(current) && !_.isEqual(current[0].title, trimmedTitle) && _.some(highlights, ['title', trimmedTitle])) {
        toast.error('Title already exist');
        setLoading(false);
        return;
      }
    }

    const arrGroup = [];
    if (!IsEmpty(group)) {
      group.forEach((pg) => {
        arrGroup.push({
          page_id:   pg.id,
          page_name: pg.name,
        });
      });
    }

    const payload = {
      title:        trimmedTitle,
      langZh:       langZh || null,
      pageId:       page.id || null,
      pageName:     page.name || null,
      group:        arrGroup || null,
      displayOrder: highlights.length + 1,
    };

    if (!isUpdating) {
      await addHighlight(payload)
        .then(() => {
          toast.success('Highlight created.');
          setLoading(false);
          setRefresh(true);
        })
        .catch((err) => {
          toast.error(err.message);
          setLoading(false);
        });
    } else {
      delete payload.displayOrder;
      await editHighlight(highlightId, payload)
        .then(() => {
          toast.success('Highlight updated.');
          setRefresh(true);
          setLoading(false);
        })
        .catch((err) => {
          toast.error(err.message);
          setLoading(false);
        });
    }

    handleCloseAdd();
  };

  const onDeleteHighlight = async (selected) => {
    await deleteHighlight(selected.id)
      .then(async () => {
        const highlightData = await apiService.getMixedHighlights();

        if (highlightData) {
          const data = highlightData.filter((highlight) => highlight.displayOrder > selected.displayOrder);
          let filteredData = [];

          if (!IsEmpty(data)) {
            data.forEach((highlight) => {
              delete highlight.group;
              highlight.displayOrder -= 1;
            });

            filteredData = data;
          } else {
            highlightData.forEach((highlight) => {
              delete highlight.group;
            });

            filteredData = highlightData;
          }

          await editHighlights(filteredData);
          setRefresh(true);
        }
      })
      .catch((err) => {
        toast.error(err.message);
      });
  };

  const onEditOrder = async () => {
    const order = sorted.map((h) => h.id);
    await updateDisplayOrder(order).then((res) => {
      if (res) {
        toast.success('Display order updated.');
        setRefresh(true);
      }
    });
    handleCloseEdit();
  };

  useEffect(() => {
    fetchHighlights();
    setRefresh(false);
  }, [refresh]);

  useEffect(() => {
    fetchViewPages();
  }, []);

  useEffect(() => {
    fetchList();
  }, [isMultipleHighlights, isUpdating]);

  return (
    <div>
      <div className="content-header">
        <h1>Highlights</h1>
        <div>
          <Button onClick={handleOpenAdd} variant="contained">Add</Button>
          &nbsp;
          {highlights.length > 1
            ? <Button onClick={handleOpenEdit} variant="contained">Edit Display Order</Button>
            : (
              <Tooltip title="Insufficient Highlights">
                <span>
                  <Button disabled variant="contained">Edit Order</Button>
                </span>
              </Tooltip>
            )}
        </div>
      </div>

      {!refresh && <HighlightTable highlights={highlights} handleDelete={(data) => onDeleteHighlight(data)} handleUpdate={(data) => onEditHighlight(data)} />}

      <Dialog fullWidth maxWidth="sm" open={openAdd} onClose={handleCloseAdd} disableBackdropClick aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {isUpdating ? 'Update' : 'Add'}
          {' '}
          Highlight
        </DialogTitle>
        <DialogContent>
          {selection.length < 1
            ? <Typography variant="subtitle1">There are no available view-typed pages to add into highlights.</Typography>
            : (
              <div>
                <TextField
                  autoFocus
                  autoComplete="off"
                  margin="dense"
                  id="title"
                  name="title"
                  label="Title *"
                  value={title}
                  onChange={(e) => handleChange(e)}
                  fullWidth
                />

                <FormGroup className="mt-3">
                  <TextField
                    autoComplete="off"
                    margin="dense"
                    id="langZh"
                    name="langZh"
                    label="Chinese (zh-SG)"
                    value={langZh}
                    onChange={(e) => handleChange(e)}
                    fullWidth
                  />
                </FormGroup>

                <FormGroup className="mt-3">
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={isMultipleHighlights}
                        onClick={() => {
                          setMultipleHighlights(!isMultipleHighlights);

                          // rest the values;
                          setGroup([]);
                          setPage('');
                        }}
                      />
                    )}
                    label="Do you want to create as group highlight ?"
                  />
                </FormGroup>

                <Autocomplete
                  className={`d-${isMultipleHighlights ? 'block' : 'none'}`}
                  disableCloseOnSelect
                  id="combo-box-demo"
                  options={selection}
                  getOptionLabel={(option) => option.name}
                  noOptionsText="This page does not exist."
                  fullWidth
                  multiple
                  onChange={(e, value, reason) => setHighlightData(e, value, reason)}
                  value={group || []}
                  renderInput={(params) => <TextField {...params} label="Select Pages *" />}
                />

                <Autocomplete
                  className={`d-${isMultipleHighlights ? 'none' : 'block'}`}
                  id="combo-box-demo"
                  options={selection}
                  getOptionLabel={(option) => option.name}
                  noOptionsText="This page does not exist."
                  fullWidth
                  onChange={(e, value, reason) => setHighlightData(e, value, reason)}
                  value={page || null}
                  renderInput={(params) => <TextField {...params} label="Select Page *" />}
                />
              </div>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAdd} color="primary">Cancel</Button>

          { !loading ? (
            <>
              { !IsEmpty(selection) && isUpdating
                ? <Button onClick={onAddHighlight} color="primary">Update</Button>
                : <Button onClick={onAddHighlight} color="primary">Add</Button> }
            </>
          ) : <CircularProgress /> }

        </DialogActions>
      </Dialog>
      <Dialog classes={{ paper: classes.modal }} fullWidth maxWidth="sm" open={openEdit} onClose={handleCloseEdit} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">Edit Display Order</DialogTitle>
        <DialogContent id="form-dialog-content">
          <HighlightSortable
            data={highlights}
            handleSort={handleSort}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} color="primary">Cancel</Button>
          <Button onClick={onEditOrder} color="primary">Update</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = (state) => ({
  token: state.auth[process.env.REACT_APP_TOKEN_NAME],
});

export default connect(mapStateToProps, { addHighlight, editHighlight, deleteHighlight, editHighlights, getPagesByType, updateDisplayOrder })(HighlightsList);
